import React from 'react';
import { Box, Paper, Typography, Button, Alert, AlertTitle } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * @component EmailHistoryErrorBoundary
 * @description Error boundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the entire page
 */
class EmailHistoryErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('EmailHistory Error Boundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Could also send to error reporting service here
    // e.g., Sentry, LogRocket, etc.
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback, showDetails } = this.props;
      
      // If custom fallback is provided, render it
      if (fallback) {
        return fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          reset: this.handleReset,
        });
      }

      // Default fallback UI
      return (
        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ErrorOutlineIcon 
              sx={{ fontSize: 64, color: 'error.main', mb: 2 }} 
            />
            
            <Typography variant="h5" gutterBottom color="error">
              Something went wrong
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              An unexpected error occurred while loading this section.
              Please try again or refresh the page.
            </Typography>

            {showDetails && this.state.error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <AlertTitle>Error Details</AlertTitle>
                <Typography variant="body2" component="pre" sx={{ 
                  whiteSpace: 'pre-wrap', 
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                }}>
                  {this.state.error.toString()}
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              <Button
                variant="contained"
                onClick={this.handleReload}
              >
                Refresh Page
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * @component withEmailHistoryErrorBoundary
 * @description Higher-order component that wraps a component with EmailHistoryErrorBoundary
 * @param {React.ComponentType} WrappedComponent - Component to wrap
 * @param {Object} [boundaryProps] - Props to pass to the error boundary
 * @returns {React.ComponentType} Wrapped component
 */
export function withEmailHistoryErrorBoundary(WrappedComponent, boundaryProps = {}) {
  return function WithErrorBoundary(props) {
    return (
      <EmailHistoryErrorBoundary {...boundaryProps}>
        <WrappedComponent {...props} />
      </EmailHistoryErrorBoundary>
    );
  };
}

export default EmailHistoryErrorBoundary;

